import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { GroupSwitcher } from './GroupSwitcher';
import { BRAND_KITS_UI_VISIBLE } from '@/features/brandKits/brandKitsGate';
import { HaiIcon, type IconName } from './icons';
import { SidebarAccountMenu } from './SidebarAccountMenu';

/**
 * AppSidebar — R164 大改版全站側邊欄(取代 HomeSidebar + TopNav)。
 * -----------------------------------------------------------------------------
 * 對齊 D3 `專案_工作區.html` lines 636-706(2026-07-29 結構性改版):
 *
 *   ┌──────────────────┐
 *   │ [H] HAi Creative ⇤│  ← 品牌區(56px;collapsed hover 顯展開鈕)
 *   │ ┌──────────────┐ │
 *   │ │D 工作區       │ │  ← 工作區切換器(GroupSwitcher;dropdown 含
 *   │ │  DenimCo    ▾ │ │     建立新工作區 / 工作區設定 / 成員入口)
 *   │ └──────────────┘ │
 *   │ 首頁             │
 *   │ 專案             │  ← nav 主段 4 項
 *   │ 素材庫           │
 *   │ 成效報表         │
 *   │ ── 設定 ──       │
 *   │ 品牌規範         │  ← 設定段 2 項
 *   │ 廣告串接         │
 *   │ ⋮                │
 *   │ (A) Alpha       ↕│  ← 帳號選單(個人資料 / 說明 / 語言 / 登出)
 *   └──────────────────┘
 *
 * 舊 TopNav(logo / GroupSwitcher / 用量 chip / Bell / ThemeMenu)整列退場:
 * logo 與 GroupSwitcher 移入本欄、用量 chip 隨「前端不顯示點數」拍板移除、
 * Bell 無新版對應移除、ThemeMenu 改造為 SidebarAccountMenu(底部)。
 *
 * Collapsed 記憶:localStorage `hai.sidebarCollapsed`('1' / '0')優先;
 * 沒存過時 fallback `initialCollapsed`(AppShell 依 route 推導:/home 展開、
 * 其他頁收合 — 沿用 Bryan 既有拍板)。
 *
 * Nav counts(D3 顯「專案 5 / 素材庫 48」):BE 無全域 aggregate endpoint,
 * 誠實真值原則不偽造 — 待 endpoint ship 後再顯。
 */

export const SIDEBAR_WIDTH_EXPANDED = 216;
export const SIDEBAR_WIDTH_COLLAPSED = 64;

/**
 * 側欄目前寬度的 CSS variable(R258)。
 *
 * **為什麼需要它**:側欄是 `position: fixed` 且 `z-index: 60`,而頁面裡有 `position: fixed`
 * 錨在視窗左緣的元素(素材庫的批次操作列 `left: 24px`)。那些元素的左半會**被側欄蓋掉** ——
 * 而且收合時一樣被吃,因為左下角的帳號區塊也在側欄裡。
 *
 * collapsed 這個 state 住在本元件(含 localStorage 記憶),AppShell 與各頁面都拿不到,
 * 所以由**側欄自己公布**寬度:唯一的 writer、語意清楚(「側欄現在多寬」是全域事實)。
 *
 * 用法:`left: calc(var(--hai-sidebar-w, 0px) + 24px)`。
 * ⚠️ fallback **必須是 `0px`** —— 沒有側欄的頁面(登入頁)不該憑空多出一段左邊距。
 */
export const SIDEBAR_WIDTH_CSS_VAR = '--hai-sidebar-w';

const COLLAPSED_STORAGE_KEY = 'hai.sidebarCollapsed';

function readStoredCollapsed(): boolean | null {
  try {
    const v = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (v === '1') return true;
    if (v === '0') return false;
    return null;
  } catch {
    return null;
  }
}

type NavIconKind = 'home' | 'projects' | 'assets' | 'reports' | 'brand' | 'ads';

interface NavLinkSpec {
  to: string;
  labelKey: string;
  icon: NavIconKind;
}

/** 主段 4 項(D3 lines 674-677)。 */
const MAIN_LINKS: NavLinkSpec[] = [
  { to: '/home', labelKey: 'sidebar.link.home', icon: 'home' },
  { to: '/projects', labelKey: 'sidebar.link.projects', icon: 'projects' },
  { to: '/assets', labelKey: 'sidebar.link.assets', icon: 'assets' },
  { to: '/ads/reports', labelKey: 'sidebar.link.reports', icon: 'reports' },
];

/** 「設定」段 2 項(D3 lines 679-681)。
 *  R397 — 品牌規範入口由 {@link BRAND_KITS_UI_VISIBLE} 蓋住(owner 拍板隱藏,授權才開)。 */
const SETUP_LINKS: NavLinkSpec[] = [
  ...(BRAND_KITS_UI_VISIBLE
    ? [{ to: '/brand-kits', labelKey: 'sidebar.link.brandKits', icon: 'brand' } as NavLinkSpec]
    : []),
  { to: '/ads/accounts', labelKey: 'sidebar.link.adAccounts', icon: 'ads' },
];

export interface AppSidebarProps {
  /**
   * localStorage 沒存過 collapsed 狀態時的初始值(AppShell 依 route 推導)。
   * 存過就以存值為準(D3 `D3_sidebarCollapsed` persist 慣例)。
   */
  initialCollapsed?: boolean;
}

export function AppSidebar({ initialCollapsed = false }: AppSidebarProps) {
  const { t } = useTranslation('home');
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(
    () => readStoredCollapsed() ?? initialCollapsed,
  );

  /**
   * 把目前寬度公布給全站(見 {@link SIDEBAR_WIDTH_CSS_VAR})。
   *
   * ⚠️ unmount 要清掉:登入頁沒有側欄,殘留的值會讓那些 `calc()` 憑空多出一段左邊距。
   */
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      SIDEBAR_WIDTH_CSS_VAR,
      `${collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED}px`,
    );
    return () => {
      root.style.removeProperty(SIDEBAR_WIDTH_CSS_VAR);
    };
  }, [collapsed]);

  const toggle = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* storage 不可用(private mode)— 僅本次 session 生效 */
      }
      return next;
    });
  }, []);

  const isActive = (to: string): boolean => {
    if (to === '/home') return pathname === '/home' || pathname === '/';
    return pathname === to || pathname.startsWith(`${to}/`);
  };

  return (
    <aside
      data-testid="app-sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
      style={{
        width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
        flexShrink: 0,
        background: 'var(--ink-900)',
        borderRight: '1px solid var(--hairline)',
        display: 'flex',
        flexDirection: 'column',
        /* 側欄**釘在視窗**,不隨文件長高。
         *
         * AppShell 是 `min-height:100vh` 的 flex row,長頁(素材庫 / 成效報表)內容一撐開,
         * 容器高度 = 文件高度、側欄 `align-items:stretch` 跟著長到同高 → 底部帳號區塊被推到
         * **文件最底**(實測 3825px 高的頁面上 y≈3772),使用者在任何捲動位置都看不到它。
         * `sticky + height:100vh` 讓它恆等於一個視窗高並跟著捲動;`mainFillHeight` 的頁
         * (canvas)容器本來就剛好 100vh,行為不變。
         * ⚠️ 不要改回 `position:relative` —— dropdown(工作區切換器 / 帳號選單)的絕對定位
         * 也掛在這層,sticky 同樣是定位參考點,但 relative 會把帳號區塊還原成看不見。 */
        position: 'sticky',
        top: 0,
        height: '100vh',
        alignSelf: 'flex-start',
        transition: 'width .2s ease',
        zIndex: 60,
      }}
    >
      <BrandRow collapsed={collapsed} onToggle={toggle} />

      {/* 工作區切換器(D3 .sb-ws)— dropdown 內含 建立新工作區 / 工作區設定 / 成員 */}
      <div style={{ padding: collapsed ? '8px 6px' : '6px 10px 8px' }}>
        <GroupSwitcher collapsed={collapsed} />
      </div>

      <nav
        aria-label={t('sidebar.navAria')}
        style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '4px 8px 8px' }}
      >
        {MAIN_LINKS.map((l) => (
          <SidebarNavRow key={l.to} spec={l} active={isActive(l.to)} collapsed={collapsed} />
        ))}

        {/* 分隔線 + 「設定」section header(D3 .sb-div / .sb-section) */}
        <div
          aria-hidden
          style={{ height: 1, background: 'var(--slate-100)', margin: '6px 12px' }}
        />
        {!collapsed && (
          <div
            style={{
              padding: '2px 12px 6px',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '.08em',
              color: 'var(--ink-500)',
              whiteSpace: 'nowrap',
            }}
          >
            {t('sidebar.section.setup')}
          </div>
        )}
        {SETUP_LINKS.map((l) => (
          <SidebarNavRow key={l.to} spec={l} active={isActive(l.to)} collapsed={collapsed} />
        ))}
      </nav>

      {/* 底部帳號選單(D3 .sb-bottom)— 個人資料 / 說明 / 語言 / 登出 */}
      <div
        style={{
          borderTop: '1px solid var(--slate-100)',
          padding: collapsed ? '8px 6px' : 8,
        }}
      >
        <SidebarAccountMenu collapsed={collapsed} />
      </div>
    </aside>
  );
}

/**
 * 品牌區(D3 .sb-brand):logo mark + 字標 + 收合鈕。
 * Collapsed:置中 logo,hover 顯示展開 overlay(D3 .sb-expand-ov)。
 */
function BrandRow({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { t } = useTranslation('home');
  const [expandHover, setExpandHover] = useState(false);

  if (collapsed) {
    return (
      <div
        style={{
          height: 56,
          flex: '0 0 56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
        onMouseEnter={() => setExpandHover(true)}
        onMouseLeave={() => setExpandHover(false)}
      >
        <LogoMark />
        {/* hover 顯展開 overlay(蓋在 logo 上;D3 .sb-expand-ov) */}
        <button
          type="button"
          onClick={onToggle}
          aria-label={t('sidebar.expand')}
          data-testid="app-sidebar-toggle"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--ink-900)',
            border: 'none',
            cursor: 'pointer',
            opacity: expandHover ? 1 : 0,
            transition: 'opacity .15s',
            padding: 0,
          }}
        >
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--concept-soft)',
              color: 'var(--concept-fg)',
            }}
          >
            <PanelIcon expand />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        height: 56,
        flex: '0 0 56px',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '0 14px',
      }}
    >
      <LogoMark />
      <span
        style={{
          fontWeight: 800,
          fontSize: 15,
          color: 'var(--text-strong)',
          whiteSpace: 'nowrap',
          letterSpacing: '-.01em',
        }}
      >
        HAi Creative
      </span>
      <button
        type="button"
        onClick={onToggle}
        aria-label={t('sidebar.collapse')}
        data-testid="app-sidebar-toggle"
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--concept-soft)';
          e.currentTarget.style.color = 'var(--concept-fg)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--ink-500)';
        }}
        style={{
          marginLeft: 'auto',
          width: 30,
          height: 30,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--ink-500)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          transition: 'background .12s, color .12s',
        }}
      >
        <PanelIcon />
      </button>
    </div>
  );
}

/** 28×28 圓角 9 品牌漸層橘 logo(D3 .logo-mark;品牌橘僅識別用,CIS 規範)。 */
function LogoMark() {
  return (
    <span
      aria-hidden
      style={{
        width: 28,
        height: 28,
        flex: '0 0 28px',
        borderRadius: 9,
        background: 'linear-gradient(135deg, var(--logo-grad-from), var(--logo-grad-to))',
        color: 'var(--on-accent)',
        fontWeight: 800,
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      H
    </span>
  );
}

/** 收合/展開 panel icon(D3 sbToggle / sbExpand SVG)。 */
// R210 — 收斂到 HaiIcon 單一來源(icons.js panelClose/panelOpen;線寬統一 1.6)
function PanelIcon({ expand = false }: { expand?: boolean }) {
  return <HaiIcon name={expand ? 'panelOpen' : 'panelClose'} />;
}

/** 單一 nav row(D3 .nav-link):active teal 底、hover slate-100。 */
function SidebarNavRow({
  spec,
  active,
  collapsed,
}: {
  spec: NavLinkSpec;
  active: boolean;
  collapsed: boolean;
}) {
  // i18next strict-augmented t() 不接動態 string key — 沿用全站 LooseT pattern。
  const { t: tRaw } = useTranslation('home');
  const t = tRaw as unknown as (key: string) => string;
  const label = t(spec.labelKey);

  return (
    <Link
      to={spec.to}
      title={collapsed ? label : undefined}
      data-testid={`app-sidebar-link-${spec.to.replace(/\//g, '-').slice(1)}`}
      onMouseEnter={(e) => {
        if (active) return;
        e.currentTarget.style.background = 'var(--slate-100)';
        e.currentTarget.style.color = 'var(--text-strong)';
      }}
      onMouseLeave={(e) => {
        if (active) return;
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = 'var(--ink-300)';
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: collapsed ? 0 : 10,
        padding: collapsed ? '8px 0' : '8px 12px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderRadius: 8,
        fontSize: 13,
        // D3 `.nav-link.active`:teal-50 底 + teal-700 字(CIS 選取態)。teal 當字走 --concept-fg:
        // --tide-primary-hover 深色是 #12897E,在深色選取底上只有 ~3.4:1(R393)
        color: active ? 'var(--concept-fg)' : 'var(--ink-300)',
        background: active ? 'var(--concept-soft)' : 'transparent',
        fontWeight: active ? 700 : 400,
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'background-color .12s, color .12s',
        marginBottom: 2,
        whiteSpace: 'nowrap',
      }}
    >
      <NavIcon kind={spec.icon} active={active} />
      {!collapsed && <span style={{ flex: '0 1 auto', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>}
    </Link>
  );
}

/** D3 nav SVG icons(lines 674-681 原 path;stroke currentColor)。 */
// R210 — 收斂到 HaiIcon 單一來源(路徑本就與 icons.js 逐字同源;線寬 1.4→1.6 對齊
// CIS「同一動作同一畫法線寬」;active 色由外層 color 提供,HaiIcon 走 currentColor)
const NAV_ICON_NAME = {
  home: 'home',
  projects: 'folder',
  assets: 'lib',
  reports: 'chart',
  brand: 'shield',
  ads: 'link',
} as const satisfies Record<NavIconKind, IconName>;

function NavIcon({ kind, active }: { kind: NavIconKind; active: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        flexShrink: 0,
        // D3 `.nav-link.active>svg`:淺 #0D9488 / 深 #2DD4BF(= 選取環 `--select-ring`;
        // `--tide-600` 在深色主題是壓暗的實心底色,選取底上的 icon 只剩 ~2.6:1)
        color: active ? 'var(--select-ring)' : undefined,
      }}
      aria-hidden
    >
      <HaiIcon name={NAV_ICON_NAME[kind]} />
    </span>
  );
}
